import React, { Dispatch } from 'react';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import Box, { BoxProps } from '@mui/material/Box';
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer';
import Close from 'mdi-material-ui/Close';
import CogOutline from 'mdi-material-ui/CogOutline';
import { Settings } from 'src/@core/context/settingsContext';
import { useSettings } from 'src/@core/hooks/useSettings';
import ComponentList from './ComponentList';
import { MenuItem, Stack, TextField, useTheme } from '@mui/material';
import { DashboardObjectWidgetDto } from '../../../models/account';
import { VigoToggle } from '../../../@vigo/vigo-toggle/index';
import { VigoCheckBox } from '../../../@vigo/vigo-checkbox/index';

const Toggler = styled(Box)<BoxProps>(({ theme }) => ({
  right: 0,
  top: '50%',
  display: 'flex',
  cursor: 'pointer',
  position: 'fixed',
  padding: theme.spacing(2),
  zIndex: theme.zIndex.modal,
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.primary.main,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderBottomLeftRadius: theme.shape.borderRadius
}));

const Drawer = styled(MuiDrawer)<DrawerProps>(({ theme }) => ({
  width: 400,
  zIndex: theme.zIndex.modal,
  '& .MuiFormControlLabel-root': {
    marginRight: '0.6875rem'
  },
  '& .MuiDrawer-paper': {
    border: 0,
    width: 400,
    zIndex: theme.zIndex.modal,
    boxShadow: theme.shadows[9]
  }
}));

const CustomizerSpacing = styled('div')(({ theme }) => ({
  padding: theme.spacing(5, 6),
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflow: 'hidden'
}));

interface CustomizerProps {
  open: boolean;
  setOpen: Dispatch<boolean>;
  components: DashboardObjectWidgetDto[];
}

const Customizer = ({ open, setOpen, components }: CustomizerProps) => {
  const { settings, saveSettings } = useSettings();
  const theme = useTheme();
  const { unlockDashboardLayout, compactType, preventCollisions, allowOverlap } = settings;

  const handleChange = (field: keyof Settings, value: Settings[keyof Settings] | string): void => {
    saveSettings({ ...settings, [field]: value });
  };

  return (
    <div className="customizer">
      <Toggler className="customizer-toggler" onClick={() => setOpen(true)}>
        <CogOutline sx={{ height: 20, width: 20, color: 'common.white' }} />
      </Toggler>
      <Drawer open={open} hideBackdrop anchor="right" variant="persistent" sx={{ zIndex: '9998' }}>
        <Box
          className="customizer-header"
          sx={{
            position: 'relative',
            p: theme => theme.spacing(3.5, 5),
            borderBottom: theme => `1px solid ${theme.palette.divider}`
          }}>
          <VigoTypography
            value="Dashboard Configuration"
            variant="h6"
            sx={{ fontWeight: 600, textTransform: 'uppercase' }}
          />
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              right: 20,
              top: '50%',
              position: 'absolute',
              color: 'text.secondary',
              transform: 'translateY(-50%)'
            }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <CustomizerSpacing className="customizer-body">
          {/* Unlock Dashboard */}
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <VigoTypography value="Unlock Dashboard Layout" />
            <VigoToggle
              name="unlockDashboardLayout"
              checked={unlockDashboardLayout}
              onChange={e => handleChange('unlockDashboardLayout', e.target.checked)}
            />
          </Box>

          {/* Compact Type */}
          <TextField
            select
            name="compactType"
            label="Compact Type"
            size="small"
            value={compactType}
            onChange={e => handleChange('compactType', e.target.value)}>
            {['horizontal', 'vertical', 'none'].map(type => (
              <MenuItem key={type} value={type}>
                <VigoTypography value={type} sx={{ textTransform: 'capitalize' }} />
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" justifyContent="space-between">
            {/* Prevent Collisions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <VigoTypography value="Prevent Collisions" />
              <VigoCheckBox
                name="preventCollisions"
                value={preventCollisions}
                sx={{
                  ...(theme.palette.mode === 'dark' && {
                    '&.Mui-checked': {
                      color: theme.palette.customColors.primaryGradient
                    }
                  })
                }}
                onChange={e => handleChange('preventCollisions', e.target.checked)}
              />
            </Box>

            {/* Allow Overlap */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <VigoTypography value="Allow Overlap" />
              <VigoCheckBox
                name="allowOverlap"
                value={allowOverlap}
                sx={{
                  ...(theme.palette.mode === 'dark' && {
                    '&.Mui-checked': {
                      color: theme.palette.customColors.primaryGradient
                    }
                  })
                }}
                onChange={e => handleChange('allowOverlap', e.target.checked)}
              />
            </Box>
          </Stack>

          <ComponentList components={components} />
        </CustomizerSpacing>
      </Drawer>
    </div>
  );
};

export default Customizer;
